import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import ApiService from '../services/apiService';

const AuthContext = createContext();

// Persists across remounts (e.g. Strict Mode). When true, we never show full-screen loading again.
let authReadyPersisted = false;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // True after a user-initiated logout: signed-out redirects then go to the main page, not /login.
  const [justLoggedOut, setJustLoggedOut] = useState(false);
  // Only false until first auth check completes; then true forever. Initial value from module so remounts don't flash loading.
  const [authReady, setAuthReady] = useState(() => authReadyPersisted);
  // { hasAccess, scope } from /api/staff/me — controls the "School Orders" page. null = not checked / no token.
  const [staffAccess, setStaffAccess] = useState(null);

  const endSession = useCallback(async () => {
    await ApiService.logout();
    setUser(null);
    setIsLoggedIn(false);
    setStaffAccess(null);
  }, []);

  const refreshStaffAccess = useCallback(async () => {
    try {
      if (!ApiService.hasSessionToken()) {
        setStaffAccess(null);
        return;
      }
      const response = await ApiService.getStaffAccess();
      setStaffAccess(response?.success ? response.data : null);
    } catch (error) {
      // Offline / old server: keep whatever we had.
    }
  }, []);

  /** Renew (or obtain) the session token in the background; log out if the server says it is over. */
  const syncSession = useCallback(async () => {
    if ((await ApiService.ensureSession()) === 'expired') {
      await endSession();
      return;
    }
    await refreshStaffAccess();
  }, [endSession, refreshStaffAccess]);

  useEffect(() => {
    ApiService.setSessionExpiredHandler(() => {
      endSession();
    });
    return () => ApiService.setSessionExpiredHandler(null);
  }, [endSession]);

  const checkAuthStatus = useCallback(async () => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        setUser(null);
        setIsLoggedIn(false);
        authReadyPersisted = true;
        setAuthReady(true);
        return;
      }
      const loggedIn = await ApiService.isLoggedIn();
      const userData = await ApiService.getUserData();
      if (loggedIn && userData) {
        setUser(userData);
        setIsLoggedIn(true);
        syncSession();
      } else {
        setUser(null);
        setIsLoggedIn(false);
      }
    } catch (error) {
      if (error?.message && !String(error.message).includes('localStorage')) {
        console.error('Error checking auth status:', error);
      }
      setUser(null);
      setIsLoggedIn(false);
    } finally {
      authReadyPersisted = true;
      setAuthReady(true);
    }
  }, [syncSession]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (email, password) => {
    try {
      const response = await ApiService.login(email, password);
      if (response?.success) {
        setUser(response.user);
        setIsLoggedIn(true);
        return { success: true, message: response.message };
      }
      return { success: false, message: response?.message || 'Login failed' };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
      };
    }
  };

  const loginMobile = async (mobileNumber, otp) => {
    try {
      const response = await ApiService.loginMobile(mobileNumber, otp);
      if (!response || typeof response !== 'object') {
        return { success: false, message: 'Login failed', userNotFound: false };
      }
      if (response.success === true && response.user) {
        setUser(response.user);
        setIsLoggedIn(true);
        return { success: true, message: response.message };
      }
      const msg = response.message != null ? String(response.message) : '';
      const userNotFound = response.userNotFound === true || /not found|register first/i.test(msg);
      return {
        success: false,
        message: response.message || 'Login failed',
        userNotFound: !!userNotFound,
      };
    } catch (error) {
      const errMsg =
        error.response?.data?.message != null
          ? String(error.response.data.message)
          : String(error.message || '');
      const userNotFound =
        /not found|register first/i.test(errMsg) || error.response?.status === 404;
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
        userNotFound: !!userNotFound,
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await ApiService.register(userData);
      if (response?.success) {
        setUser(response.user);
        setIsLoggedIn(true);
        return { success: true, message: response.message };
      }
      return { success: false, message: response?.message || 'Registration failed' };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
      };
    }
  };

  const registerMobile = async (userData) => {
    try {
      const response = await ApiService.registerMobile(userData);
      if (response?.success) {
        setUser(response.user);
        setIsLoggedIn(true);
        return { success: true, message: response.message };
      }
      return { success: false, message: response?.message || 'Registration failed' };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
      };
    }
  };

  const sendOtp = async (mobileNumber) => {
    try {
      const response = await ApiService.sendOtp(mobileNumber);
      if (response && typeof response === 'object') return response;
      return { success: false, message: 'Unexpected response from server' };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message || error.message || 'Failed to send OTP. Please try again.',
      };
    }
  };

  const logout = async () => {
    try {
      setJustLoggedOut(true);
      await endSession();
      return { success: true, message: 'Logged out successfully' };
    } catch (error) {
      return { success: false, message: 'Logout failed' };
    }
  };

  useEffect(() => {
    if (isLoggedIn) setJustLoggedOut(false);
  }, [isLoggedIn]);

  // A fresh login stores a new token: check School Orders access for it.
  useEffect(() => {
    if (isLoggedIn) refreshStaffAccess();
  }, [isLoggedIn, refreshStaffAccess]);

  const value = {
    user,
    isLoggedIn,
    justLoggedOut,
    authReady,
    login,
    loginMobile,
    register,
    registerMobile,
    sendOtp,
    logout,
    checkAuthStatus,
    staffAccess,
    refreshStaffAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
